<template>
  <b-card>
    <b-button
      id="default-filter-new-btn"
      variant="primary"
      type="button"
      size="sm"
      class="mb-2"
      @click="openCreatePopup()"
    >
      <feather-icon icon="PlusIcon" size="15" />
      <span>{{ t('Add New Record') }}</span>
      <b-tooltip
        triggers="hover"
        target="default-filter-new-btn"
        :title="t('Add New Record')"
        :delay="{ show: 0, hide: 50 }"
      />
    </b-button>

    <b-modal
      id="add-new-rate-modal"
      ref="add-new-rate-modal"
      v-model="showCreatePopup"
      cancel-variant="outline-secondary"
      :ok-title="t('Save')"
      :cancel-title="t('Close')"
      centered
      :title="t('Add New Record')"
      size="sm"
      @ok="onSaveNewRate"
    >
      <validation-observer ref="addProductForCustomValidate">
        <b-form>
          <b-form-group label-for="newRateRegionSelect">
            <label class="mb-1">{{ t('Region') }}</label>
            <b-form-select
              id="newRateRegionSelect"
              v-model="selectedRegionId"
              :options="[{ text: t('Make a Choice'), value: '' }].concat(regionOptions)"
              required
            />
          </b-form-group>

          <b-form-group label-for="newRateInput">
            <validation-provider
              #default="{ errors }"
              ref="newRateInput"
              :name="t('Rate')"
              rules="required|positiveInteger"
            >
              <b-form-input
                id="newRateInput"
                v-model="newRate"
                type="number"
                name="newRateInput"
                rules="required|positiveInteger"
                :placeholder="t('Enter Rate')"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-form>
      </validation-observer>
    </b-modal>

    <vue-good-table
      mode="remote"
      :sort-options="{ enabled: false }"
      :total-rows="totalRecords"
      :is-loading.sync="isLoading"
      :show-footer="true"
      :pagination-options="{
        enabled: true,
        jumpFirstOrLast: true,
        rowsPerPageLabel: t('Rows per page'),
        ofLabel: t('ofLabel'),
        nextLabel: t('nextLabel'),
        prevLabel: t('prevLabel'),
        dropdownAllowAll: false
      }"
      :rows="rows"
      :columns="getColumns"
      @on-page-change="onPageChange"
      @on-per-page-change="onPerPageChange"
    >
      <template slot="table-row" slot-scope="props">
        <span v-if="props.column.field === 'title'">
          {{ props.row.package.title || '-' }}
        </span>
        <span v-else-if="props.column.field === 'description'">
          {{ props.row.package.description || '-' }}
        </span>

        <span v-else-if="props.column.field === 'region'">
          {{ props.row.region.name || '-' }}
        </span>

        <span v-else-if="props.column.field === 'provider'">
          {{ props.row.provider.name || '-' }}
        </span>

        <span v-else-if="props.column.field === 'rate'">
          <div class="d-flex align-items-center justify-content-start">
            <validation-provider
              v-slot="{ errors }"
              :name="t('Rate')"
              rules="required|positiveInteger"
              class="d-flex flex-column align-items-center justify-content-start mr-2"
              style="height: 3rem"
            >
              <b-form-input v-model="props.row.rate" class="" type="number" />
              <small class="text-danger">{{ errors ? errors[0] : '' }}</small>
            </validation-provider>

            <b-button variant="primary" type="button" size="sm" @click="onSaveRate(props.row)">
              {{ t('Save') }}
            </b-button>
          </div>
        </span>

        <span v-else-if="props.column.field === 'created_at'">
          {{ props.row.created_at || '-' }}
        </span>

        <span v-else-if="props.column.field === 'updated_at'">
          {{ props.row.updated_at || '-' }}
        </span>

        <span v-else-if="props.column.field === 'actions'">
          <span class="d-flex justify-content-center table-icon-buttons">
            <b-button
              :id="`delete-button-${props.row.id}`"
              variant="danger"
              type="button"
              size="sm"
              @click="onDeleteRegion(props.row)"
            >
              <feather-icon icon="XIcon" size="15" />
              <b-tooltip
                triggers="hover"
                :target="`delete-button-${props.row.id}`"
                :title="t('Delete')"
                :delay="{ show: 0, hide: 0 }"
                boundary="viewport"
              />
            </b-button>
          </span>
        </span>
      </template>
    </vue-good-table>
  </b-card>
</template>

<script>
import {
  BButton,
  BCard,
  BForm,
  BFormInput,
  BFormGroup,
  BTooltip,
  BFormSelect,
  BModal
} from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import useService from '@/auth/service/useService';

export default {
  components: {
    BButton,
    BForm,
    BCard,
    BFormInput,
    BFormGroup,
    BTooltip,
    BFormSelect,
    BModal,

    VueGoodTable,
    ValidationProvider,
    ValidationObserver
  },

  mixins: [helper],

  data() {
    return {
      isLoading: false,
      rows: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      values: {
        status: false
      },
      showCreatePopup: false,
      newRate: null,
      isFromPopup: false,
      selectedRegionId: '',
      regionOptions: []
    };
  },

  computed: {
    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      return [
        {
          label: this.$t('Title'),
          field: 'package.title'
        },
        {
          label: this.$t('Description'),
          field: 'package.description'
        },
        {
          label: this.$t('Region'),
          field: 'region.name'
        },
        {
          label: this.$t('Provider'),
          field: 'provider.name'
        },
        {
          label: this.$t('Rate'),
          field: 'rate'
        },
        {
          label: this.$t('Created At'),
          field: 'created_at'
        },
        {
          label: this.$t('Updated At'),
          field: 'updated_at'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },

  async created() {
    const { packageId } = this.$route.params;
    const { providerId } = this.$route.params;
    this.loadItems(packageId, providerId);
    this.getAllRegions();

    extend('positiveInteger', {
      validate: value => {
        const intValue = parseInt(value, 10);
        return intValue >= 0 && Number.isInteger(intValue);
      },
      message: this.$t('Negative value cannot be entered')
    });
  },

  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    loadItems(packageId, providerId) {
      this.isLoading = true;

      useService
        .getPackageProvider(packageId, providerId)
        .then(response => {
          this.rows = response.data;
          this.totalRecords = response.meta.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    onDeleteRegion(row) {
      const packageId = row.package ? row.package.id : null;
      const regionId = row.region ? row.region.id : null;
      const providerId = row.region ? row.provider.id : null;

      if (packageId !== null && regionId !== null) {
        this.deleteRegion(packageId, regionId, providerId);
      } else {
        this.showErrorMessage(this.$t('Package or Region Information is Missing'));
      }
    },

    deleteRegion(packageId, regionId, providerId) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Delete Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await useService
            .deletePackageProvider(packageId, regionId)
            .then(response => {
              this.showSuccessMessage(response.message);

              this.filter.page = 1;
              this.loadItems(packageId, providerId);
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            });
        }
      });
    },

    onSaveRate(row) {
      if (row.rate < 0) {
        return;
      }
      this.submitData(row.package.id, row.region.id, row.rate, false);
    },

    onSaveNewRate() {
      const packageId = parseInt(this.$route.params.packageId);
      const rate = parseFloat(this.newRate);
      const regionId = this.selectedRegionId;
      if (rate < 0) {
        return;
      }
      this.submitData(packageId, regionId, rate, true);
    },

    submitData(packageId, regionId, rate, isFromPopup) {
      this.submitting = true;

      const { providerId } = this.$route.params;

      const requestData = {
        rate: parseFloat(rate).toFixed(3),
        package_id: packageId
      };

      if (regionId !== null) {
        requestData.region_id = regionId;
      }

      useService
        .createPackageProvider(packageId, requestData)
        .then(response => {
          this.showSuccessMessage(response.message);
        })
        .catch(messages => {
          if (typeof messages === 'object' && messages && messages.length) {
            messages.forEach(item => {
              this.showErrorMessage(item.message);
            });
          }
        })
        .finally(() => {
          this.submitting = false;
          if (isFromPopup) {
            this.showCreatePopup = false;
            this.loadItems(packageId, providerId);
          }
        });
    },

    openCreatePopup() {
      this.showCreatePopup = true;
    },

    async getAllRegions() {
      try {
        const { providerId } = this.$route.params;
        const response = await useService.getAllRegions(providerId);
        const regions = response.data;

        this.regionOptions = regions.map(region => ({
          value: region.id,
          text: region.name
        }));
      } catch (error) {
        this.showErrorMessage(error);
      }
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
